// src/lib/utils/invoice.ts
import { currencySymbols, GstType } from '../constants/invoice';
import { formatDateToYYYYMMDD } from './date';

// Types
export interface GSTItem {
  rate: number;
  quantity: number;
  taxRate?: number | string | null;
  taxValue?: string;
}

export interface TaxLine {
  label: 'CGST' | 'SGST' | 'IGST';
  rate: number;
  amount: number;
}

type CurrencyCode = 'INR' | 'USD' | 'EUR' | string;

const round2 = (n: number) => Math.round(n * 100) / 100;

// Same state => CGST + SGST, different state => IGST
export const getGstType = (companyState?: string, placeOfSupply?: string): GstType =>
  companyState && placeOfSupply && companyState !== placeOfSupply ? 'IGST' : 'CGST_SGST';

// Parse an item's taxValue ('CGST_SGST_18', 'IGST_18', 'out_of_scope', ...).
// Older invoices stored only the rate ('18'), so those fall back to the place-of-supply type.
export const parseTaxValue = (
  taxValue: string | undefined,
  fallbackType: GstType
): { gstType: GstType | null; rate: number } => {
  const value = taxValue || '';

  const match = value.match(/^(CGST_SGST|IGST)_(\d+(?:\.\d+)?)$/);
  if (match) return { gstType: match[1] as GstType, rate: Number(match[2]) };

  if (/^\d+(\.\d+)?$/.test(value)) return { gstType: fallbackType, rate: Number(value) };

  return { gstType: null, rate: 0 };
};

// GST Breakdown as CGST / SGST / IGST lines
export const calculateTaxBreakdown = (items: GSTItem[] = [], fallbackType: GstType): TaxLine[] => {
  const groups: Record<string, { gstType: GstType; rate: number; tax: number }> = {};

  items.forEach((item) => {
    const rate = Number(item.taxRate) || 0;
    if (rate <= 0) return;

    const gstType = parseTaxValue(item.taxValue, fallbackType).gstType ?? fallbackType;
    const key = `${gstType}_${rate}`;
    const tax = (item.rate * item.quantity * rate) / 100;

    groups[key] = groups[key] ?? { gstType, rate, tax: 0 };
    groups[key].tax += tax;
  });

  const cgstSgstLines: TaxLine[] = [];
  const igstLines: TaxLine[] = [];

  Object.values(groups)
    .sort((a, b) => a.rate - b.rate)
    .forEach(({ gstType, rate, tax }) => {
      const total = round2(tax);

      if (gstType === 'IGST') {
        igstLines.push({ label: 'IGST', rate, amount: total });
        return;
      }

      // Split equally; SGST gets the remainder so both halves always add up to the total
      const cgst = round2(total / 2);
      cgstSgstLines.push({ label: 'CGST', rate: rate / 2, amount: cgst });
      cgstSgstLines.push({ label: 'SGST', rate: rate / 2, amount: round2(total - cgst) });
    });

  return [...cgstSgstLines, ...igstLines];
};

// Tax Rate Helper
export const getTaxRate = (taxValue: unknown): number => {
  const rate = Number(taxValue);
  return Number.isFinite(rate) ? rate : 0;
};

// Number to Words
export const numberToWords = (amount: number, currency: CurrencyCode = 'INR'): string => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = [
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];

  const convertHundreds = (num: number): string => {
    let result = '';

    if (num > 99) {
      result += ones[Math.floor(num / 100)] + ' Hundred ';
      num %= 100;
    }

    if (num > 19) {
      result += tens[Math.floor(num / 10)] + ' ';
      num %= 10;
    } else if (num > 9) {
      result += teens[num - 10] + ' ';
      return result;
    }

    if (num > 0) {
      result += ones[num] + ' ';
    }

    return result;
  };

  const [rupees, paise] = amount.toFixed(2).split('.').map(Number);

  let result = '';

  if (rupees === 0) {
    result = 'Zero ';
  } else {
    const crores = Math.floor(rupees / 10000000);
    const lakhs = Math.floor((rupees % 10000000) / 100000);
    const thousands = Math.floor((rupees % 100000) / 1000);
    const hundreds = rupees % 1000;

    if (crores > 0) result += convertHundreds(crores) + 'Crore ';
    if (lakhs > 0) result += convertHundreds(lakhs) + 'Lakh ';
    if (thousands > 0) result += convertHundreds(thousands) + 'Thousand ';
    if (hundreds > 0) result += convertHundreds(hundreds);
  }

  const currencyNames: Record<string, { major: string; minor: string }> = {
    INR: { major: 'Rupees', minor: 'Paise' },
    USD: { major: 'Dollars', minor: 'Cents' },
    EUR: { major: 'Euros', minor: 'Cents' },
  };

  const currencyName = currencyNames[currency] ?? currencyNames.INR;
  result += currencyName.major;

  if (paise > 0) {
    result += ' and ' + convertHundreds(paise) + currencyName.minor;
  }

  return result.trim() + ' Only';
};

export const formatCurrency = (amount: number, currency: string = 'INR'): string => {
  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatNumberWithCommas = (value: number | string): string => {
  if (value === null || value === undefined || value === '') return '';

  const [integer, decimal] = value.toString().split('.');
  const formattedInt = Number(integer).toLocaleString('en-IN');

  return decimal ? `${formattedInt}.${decimal}` : formattedInt;
};

export const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// calculate due date based on terms
export const calculateDueDate = (invoiceDate: string, terms: string): string | null => {
  if (!invoiceDate) return null;

  const baseDate = new Date(invoiceDate);

  switch (terms) {
    case 'Custom':
      return null;

    case 'Immediate':
    case 'Due on Receipt':
    case 'COD':
    case 'Advance Payment':
      // Due immediately / same day
      return invoiceDate;

    case 'Net 10':
      baseDate.setDate(baseDate.getDate() + 10);
      break;

    case 'Net 15':
      baseDate.setDate(baseDate.getDate() + 15);
      break;

    case 'Net 30':
      baseDate.setDate(baseDate.getDate() + 30);
      break;

    case 'Net 45':
      baseDate.setDate(baseDate.getDate() + 45);
      break;

    case 'Net 60':
      baseDate.setDate(baseDate.getDate() + 60);
      break;

    case 'EOM':
      // End of Month - last day of the invoice month
      const lastDay = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
      return formatDateToYYYYMMDD(lastDay);
  }
  return formatDateToYYYYMMDD(baseDate);
};

// get terms description (for display purposes)
export const getTermsDescription = (terms: string, invoiceDate: string): string | null => {
  if (!invoiceDate) return null;

  const baseDate = new Date(invoiceDate);

  switch (terms) {
    case 'Custom':
      return 'Select due date manually';

    case 'Immediate':
      return 'Payment due immediately';

    case 'Due on Receipt':
      return 'Payment due upon receipt of invoice';

    case 'Net 10':
      return 'Payment due in 10 days';

    case 'Net 15':
      return 'Payment due in 15 days';

    case 'Net 30':
      return 'Payment due in 30 days';

    case 'Net 45':
      return 'Payment due in 45 days';

    case 'Net 60':
      return 'Payment due in 60 days';

    case 'EOM': {
      const lastDay = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
      const monthName = lastDay.toLocaleDateString('en-US', { month: 'short' });
      return `Payment due by end of ${monthName} (${lastDay.getDate()}th)`;
    }

    case 'COD':
      return 'Payment due on delivery';

    case 'Advance Payment':
      return 'Payment required in advance';

    default:
      return null;
  }
};
